import type { Schema } from '@dealdesk/api-schema';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, cn, type IconName } from '@/design-system';
import { formatDateTime } from '@/shared/lib/format';

import { MoneyValue } from '../money';
import { StatusBadge } from '../status';

const KIND_ICON: Partial<Record<Schema<'BankUpdateKind'>, IconName>> = {
  query: 'concept.bankQuery',
  document_request: 'concept.documents',
  verification: 'concept.verificationCall',
  offer: 'concept.offer',
  approval: 'concept.approval',
  rejection: 'concept.rejection',
};

/**
 * A structured bank update: kind and category, the bank's own words (verbatim), extracted amounts and outcome.
 * The bank's text is quoted and never translated; the kind decides the icon (only where the concept repeats).
 */
export function BankUpdateItem({ update, actions, className }: { update: Schema<'BankUpdate'>; actions?: ReactNode; className?: string }) {
  const { t } = useTranslation();
  const open = update.outcome === 'open';
  const icon = KIND_ICON[update.kind];
  return (
    <div className={cn('flex flex-col gap-1 border-b border-border py-2.5 last:border-b-0', className)}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-body">
        <span className={cn('inline-flex items-center gap-1.5 font-medium', open ? 'text-warning' : update.kind === 'rejection' ? 'text-negative' : 'text-ink')}>
          {icon ? <Icon name={icon} size="xs" tone={open ? 'warning' : 'muted'} /> : null}
          {t(`bankUpdate.${update.kind}`, { ns: 'status' })}
        </span>
        {update.categoryLabel ? <span className="text-meta text-ink-secondary">{update.categoryLabel}</span> : null}
        {update.source ? <span className="text-meta text-ink-muted">{t('records.bankSource', { source: update.source })}</span> : null}
        {update.outcome !== 'noted' ? <StatusBadge registry="bankUpdateOutcome" value={update.outcome} className="text-meta" /> : null}
        {update.imported ? <span className="text-meta text-ink-muted">{t('workflow.update.imported')}</span> : null}
        <span className="ms-auto text-meta whitespace-nowrap text-ink-muted">{formatDateTime(update.receivedAt)}</span>
      </div>
      {update.summary ? (
        <p className="text-body font-medium text-ink" dir="auto">
          {update.summary}
        </p>
      ) : null}
      <blockquote dir="auto" className="border-s-2 border-border-strong ps-2.5 text-body whitespace-pre-wrap text-ink">
        {update.bankText}
      </blockquote>
      {update.eligibleAmount || update.shortfallAmount || update.verificationOutcome ? (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-meta text-ink-secondary">
          {update.eligibleAmount ? (
            <span className="inline-flex items-baseline gap-1">
              {t('workflow.update.eligible')} <MoneyValue value={update.eligibleAmount} showCurrency="prefix" />
            </span>
          ) : null}
          {update.shortfallAmount ? (
            <span className="inline-flex items-baseline gap-1">
              {t('workflow.update.shortfall')} <MoneyValue value={update.shortfallAmount} showCurrency="prefix" />
            </span>
          ) : null}
          {update.verificationOutcome ? <span>{t('workflow.update.verification', { outcome: t(`workflow.update.verificationOutcome.${update.verificationOutcome}`), attempt: update.attempt ?? 1 })}</span> : null}
        </div>
      ) : null}
      {update.requiredAction && open ? <p className="text-meta text-ink-secondary">{t('workflow.update.requiredAction', { action: update.requiredAction })}</p> : null}
      {update.resolution ? (
        <p className="text-meta text-ink-secondary" dir="auto">
          {t('workflow.update.resolution', { text: update.resolution, name: update.resolvedBy?.name ?? '' })}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-muted">
        <span>{t('records.by', { name: update.by.name })}</span>
        {update.pendingWith ? <span>{t('workflow.update.pendingWith', { label: update.pendingWith.label })}</span> : null}
        {open && update.dueAt ? <span className="font-medium text-warning">{t('records.responseDue', { time: formatDateTime(update.dueAt) })}</span> : null}
        {actions ? <span className="ms-auto">{actions}</span> : null}
      </div>
    </div>
  );
}
